import { Content } from "@tiptap/react";
import { TDefectListOptions } from "./options.model";

export enum TemplatePageType {
  coverPage = "coverPage",
  contentsPage = "contentsPage",
  defectList = "defectList",
  customPage = "customPage",
}

const taskDataKey = Symbol("task");

export type TTemplatePageData = { [taskDataKey]: true; taskId: TSectionPage["id"] };

export function getTemplatePageData(task: TSectionPage): TTemplatePageData {
  return { [taskDataKey]: true, taskId: task.id };
}

// /** Assertion */
export function isTemplatePageData(data: Record<string | symbol, unknown>): data is TTemplatePageData {
  return data[taskDataKey] === true;
}

export type TTemplateContent = {
  /** Unique Azure Document ID */
  id: string;

  /** User friendly name */
  templateName: string;

  /** Version 1 Template Option Settings */
  defaultOptions: TDefectListOptions;

  /** Report pages */
  pages: TSectionPage[];

  /** Additional report settings */
  settings: {
    reportHeader: Content | undefined;
    reportFooter: Content | undefined;
  };

  /** Recently-used colours (most recent first, capped at kMaxRecentColors) for the colour picker swatches. */
  recentColors?: string[];

  /** Every image referenced anywhere in `pages`, keyed by the stable id the page nodes carry.
   *  Absent on documents saved before this was introduced — see `migrateLegacyImageReferences`. */
  imageManifest?: TImageManifestEntry[];

  /** Document schema version. `1` is the first version to carry `imageManifest`. Doubles as the
   *  migration boundary for legacy (pre-manifest) documents. */
  schema?: number;
};

/** One entry in `TTemplateContent.imageManifest`. `id` is the stable GUID page nodes reference —
 *  it never changes, so duplicating a page/section that contains an image keeps both copies
 *  pointing at the same entry (and the same underlying blob) rather than re-uploading. */
export type TImageManifestEntry = {
  id: string;
  /** `Documents.Id` returned by `POST /Photos`, used to delete the blob. */
  docId: number;
  filename: string;
  /** Full blob URL (storage-token query string stripped), exactly as returned by `POST /Photos`
   *  at upload time. There's no blob-container base URL available anywhere in this app's config —
   *  the resolved URL has only ever come from the upload API response — so it's captured here
   *  rather than reconstructed from `docId`/`filename` at display time. */
  url: string;
};

export type TSectionPage = {
  /** Unique ID for managing page position with Grabbable tool */
  id: string;

  /** Cover, Contents, Defect List, etc */
  type: TemplatePageType;

  /** Tiptap Editor content */
  content?: Content;
};
